import React from 'react';

import { Translate } from 'react-localize-redux';
import AppState from '@webapp/store/state';
import InvitesListCtrl from '@webapp/controllers/invites/invites-list';
import LoadingCtrl from '@webapp/controllers/loading';
import { RecordLabelState } from '@webapp/store/record-labels/state';
import {
  CreateOperatorInvite,
  RemoveOperatorFromRecordLabel,
} from '@webapp/store/record-labels/action-creators';
import { LIST_RECORD_LABEL_ARTIST } from '@webapp/store/record-labels/actions';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Operator } from '@services/record-labels/types/operator';
import InviteOperatorRequest from '@common/types/record-label/invite-operator-request';
import User from '@common/types/user/user';
import { RecordLabel } from '@common/types/record-label/record-label';
import OperatorLabelInviteCtrl from '../../../label/operator-label-invite';
import { UserSearchResult } from '@webapp/types/user-search-result';
import { normalizePhoneOrEmail } from '@webapp/utils/normalizers';
import OperatorItem from '../operator-item';

interface OperatorsProps {
  className?: string;
  operators: RecordLabelState['operators'];
  newInvite: (
    recordLabelId: string,
    invite: InviteOperatorRequest
  ) => CreateOperatorInvite;
  removeOperator: (
    labelId: string,
    userId: string
  ) => RemoveOperatorFromRecordLabel;
  isLoading: boolean;
  recordLabel: RecordLabel;
  ownerId: string;
  canEdit: boolean;
  inviter?: Partial<User>;
}

const mapStateToProps = (state: AppState, props: Partial<OperatorsProps>) => ({
  operators: state.recordLabels.operators,
  inviter: state.user.profile,
  isLoading: !!state.ui.loading[LIST_RECORD_LABEL_ARTIST],
  canEdit: props.ownerId === state.user.profile?.id,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  removeOperator: (labelId: string, userId: string) =>
    dispatch(new RemoveOperatorFromRecordLabel(labelId, userId)),
  newInvite: (recordLabelId: string, invite: InviteOperatorRequest) =>
    dispatch(new CreateOperatorInvite(recordLabelId, invite)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(function Operators(props: OperatorsProps) {
  const [newOperator, setNewOperator] =
    React.useState<Partial<UserSearchResult>>();
  const getLabelOperators = (recordLabelId: string) => {
    return props.operators[recordLabelId] || [];
  };

  const operatorAsUser = (operator: Operator): UserSearchResult => {
    return {
      id: operator.userId,
      name: operator.userName,
      avatar: '',
      contact: '',
      type: operator.userId ? 'user' : 'invite',
      helpText: operator.userName,
    };
  };

  const handleSelection = (operator: UserSearchResult) => {
    setNewOperator(undefined);
    const contact = normalizePhoneOrEmail(operator.contact);

    if (!contact) {
      return;
    }

    if (props.inviter) {
      const newInviteOp: InviteOperatorRequest = {
        inviter: props.inviter,
        userId: operator.id,
        contactType: contact.type,
        name: operator.name,
        address: contact.value,
      };
      props.newInvite(props.recordLabel.id, newInviteOp);
      setNewOperator(undefined);
    }
  };

  return (
    <>
      {!props.isLoading ? (
        <>
          {props.canEdit && (
            <OperatorLabelInviteCtrl
              label={<Translate id="USER_PROFILE.ROSTER.OPERATOR.NEW" />}
              className={`${
                getLabelOperators(props.recordLabel.id).length >= 1
                  ? 'mb-40'
                  : ''
              }`}
              value={newOperator as UserSearchResult}
              currentValues={getLabelOperators(props.recordLabel.id).map(
                operatorAsUser
              )}
              role="operator"
              type="operator"
              locked={props.isLoading}
              recordLabel={props.recordLabel}
              onChange={(value) =>
                setNewOperator({ ...newOperator, name: value })
              }
              onSelection={(operator) => handleSelection(operator)}
              songName=""
            />
          )}
          {getLabelOperators(props.recordLabel.id).map((operator) => (
            <OperatorItem
              key={operator.userId}
              name={operator.userName}
              labelId={operator.recordLabelId}
              userId={operator.userId}
              onRemove={props.removeOperator}
            />
          ))}
          <InvitesListCtrl
            title="Your invites"
            type="operator"
            recordLabelId={props.recordLabel.id}
          />
        </>
      ) : (
        <div className="loader-container">
          <LoadingCtrl
            size="100px"
            className="loading-animation"
            loading={props.isLoading}
          />
        </div>
      )}
    </>
  );
});
